import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import type { WaterPriceListCompleteness } from '@models/price-list-completeness';
import type { WaterPriceList, WaterPriceListCreateOrUpdate } from '@models/water-price-list';
import { finalize, type Observable, Subject, shareReplay, tap } from 'rxjs';

/** A price list that was just created, and the lake it belongs to. */
export interface CreatedPriceList {
  waterId: number;
  priceList: WaterPriceList;
}

/**
 * A lake's price lists (CS-232). Plain per-row CRUD: the set is small — roughly one entry per year —
 * and a delete is destructive enough (it cascades away that year's entire pricing) that it has to be a
 * deliberate act rather than a row dropped from a whole-set replace.
 *
 * The listing is ordered newest-first by the API, which every price-list dropdown relies on to default
 * to the latest year.
 */
@Injectable({ providedIn: 'root' })
export class CrmWaterPriceListsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/api/crm/waters`;

  /** The listing request currently in flight per water, if any — see {@link getPriceLists}. */
  private readonly priceListRequests = new Map<number, Observable<WaterPriceList[]>>();

  private readonly createdSubject = new Subject<CreatedPriceList>();

  /**
   * An in-flight request is shared, so callers subscribing in the same tick get one GET between them. The
   * Pricing tab has two independent readers of this listing — the Price lists section and the exclusive
   * grid's dropdown — which both load on render and both re-read after a write, so without this every one
   * of those moments costs two identical GETs.
   *
   * Sharing lasts only as long as the request. This must not become a cache: the listing is written from
   * the very screen that displays it, so a response outliving its request would show a price list that has
   * since been renamed or deleted, and a retained *error* would make the sections' retry buttons no-ops.
   *
   * The map entry is what bounds it, and it has to be — `shareReplay` cannot. Its reset is hardcoded to
   * `resetOnComplete: false`, and `refCount`'s reset is skipped once the source has completed, which for an
   * HTTP GET is immediately; the buffered response would then be replayed for the rest of the session.
   * Dropping the entry in `finalize` instead makes the settled observable unreachable, so the next call
   * builds a new one and really fetches. `finalize` sits above `shareReplay` to run once, on the shared
   * source, rather than per subscriber.
   */
  getPriceLists(waterId: number): Observable<WaterPriceList[]> {
    let request = this.priceListRequests.get(waterId);
    if (request === undefined) {
      request = this.http.get<WaterPriceList[]>(this.priceListsUrl(waterId)).pipe(
        finalize(() => this.priceListRequests.delete(waterId)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.priceListRequests.set(waterId, request);
    }
    return request;
  }

  /**
   * A price list that has just been stored, for the sections listing the same set to take in without
   * re-reading it. Announced here rather than passed up from the button that created it: the Water form
   * carries that button in several places — the Price lists card, and beside each pricing grid's dropdown,
   * including the empty state a lake with no price lists shows — and each of those would otherwise have to
   * relay the event to its siblings, which is how a newly created price list ends up missing from the table
   * above it.
   */
  readonly created$ = this.createdSubject.asObservable();

  /**
   * How completely each of the lake's price lists is priced, and which grids are short. Deliberately a
   * second request rather than fields on {@link getPriceLists}: every price-list dropdown on the page
   * reads that listing and none of them needs this, and the indicator may arrive after the table has
   * already drawn its rows.
   */
  getCompleteness(waterId: number): Observable<WaterPriceListCompleteness[]> {
    return this.http.get<WaterPriceListCompleteness[]>(`${this.priceListsUrl(waterId)}/completeness`);
  }

  createPriceList(waterId: number, request: WaterPriceListCreateOrUpdate): Observable<WaterPriceList> {
    return this.http
      .post<WaterPriceList>(this.priceListsUrl(waterId), request)
      .pipe(tap((priceList) => this.createdSubject.next({ waterId, priceList })));
  }

  updatePriceList(
    waterId: number,
    priceListId: number,
    request: WaterPriceListCreateOrUpdate,
  ): Observable<WaterPriceList> {
    return this.http.put<WaterPriceList>(`${this.priceListsUrl(waterId)}/${priceListId}`, request);
  }

  deletePriceList(waterId: number, priceListId: number): Observable<void> {
    return this.http.delete<void>(`${this.priceListsUrl(waterId)}/${priceListId}`);
  }

  private priceListsUrl(waterId: number): string {
    return `${this.baseUrl}/${waterId}/price-lists`;
  }
}
